import {getApiUrl} from '../../../config';
import {authenticatedFetch} from '../../../services/authRuntime';
import {matrixService} from '../../../services/matrixService';

export interface IncomingChatWebhook {
    readonly id: string;
    readonly name: string;
    readonly roomId: string | null;
    readonly status: 'enabled' | 'disabled';
    readonly lastAcceptedAt: string | null;
    readonly lastDeliveredAt: string | null;
    readonly attention: string | null;
}
export interface WebhookRoom {readonly roomId: string; readonly name: string; readonly encrypted: boolean;}
export interface WebhookDelivery {
    readonly id: string; readonly createdAt: string; readonly status: string; readonly attempts: number;
    readonly errorCode: string | null; readonly eventId: string | null; readonly warnings: readonly string[];
}
export interface CreatedWebhook {readonly webhook: IncomingChatWebhook; readonly url: string;}
interface DataResponse<T> {readonly data: T;}

// Responses containing a credential stay in component memory and never enter Redux or its persistence middleware.
async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
    const base = await getApiUrl();
    if (typeof base !== 'string') throw new Error('API is not ready.');
    const token = matrixService.getClient()?.getAccessToken();
    const response = await authenticatedFetch(`${base.replace(/\/$/, '')}/api/v1/chats/webhooks${path}`, {
        ...options, cache: 'no-store', headers: {
            'Content-Type': 'application/json',
            ...(token ? {'X-Matrix-Authorization': `Bearer ${token}`} : {}),
        },
    });
    if (!response.ok) {
        const body: unknown = await response.json().catch(() => undefined);
        const code = body && typeof body === 'object' && 'error' in body && typeof body.error === 'string' ? body.error : undefined;
        throw new Error(code ?? (response.status === 404 ? 'Incoming chat webhooks are unavailable for this tenant.' : 'The webhook request failed. Check room access and try again.'));
    }
    if (response.status === 204) return undefined as T;
    return response.json() as Promise<T>;
}
const data = async <T>(path: string, options?: RequestInit): Promise<T> => (await request<DataResponse<T>>(path, options)).data;
export const incomingChatWebhookApi = {
    list: () => data<readonly IncomingChatWebhook[]>(''),
    rooms: () => data<readonly WebhookRoom[]>('/rooms'),
    create: (input: {readonly name: string; readonly roomId: string}) => data<CreatedWebhook>('', {method: 'POST', body: JSON.stringify(input)}),
    rename: (id: string, name: string) => request<void>(`/${id}`, {method: 'PUT', body: JSON.stringify({name})}),
    disable: (id: string) => request<void>(`/${id}/disable`, {method: 'POST'}),
    enable: (id: string) => request<void>(`/${id}/enable`, {method: 'POST'}),
    rotate: (id: string) => data<CreatedWebhook>(`/${id}/rotate`, {method: 'POST'}),
    remove: (id: string) => request<void>(`/${id}`, {method: 'DELETE'}),
    test: (id: string) => data<{readonly deliveryId: string}>(`/${id}/test`, {method: 'POST'}),
    deliveries: (id: string, offset: number) => data<readonly WebhookDelivery[]>(`/${id}/deliveries?offset=${offset}`),
};
